import { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import { useGetPlans } from '../../../APIs/Plan/plan';
import './pricingSection.css';

export default function PricingSection({ onSelectPlan, currentPlanId, renewMode = false, error = '', onClearError }) {
  const { plans, current, isLoading } = useGetPlans();
  const [loading, setLoading] = useState(false);

  if (isLoading) {
    return (
      <div style={{ minHeight: 200, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!plans.length) {
    return <div className="text-center text-muted py-5">No plans available</div>;
  }

  const activePlanId = currentPlanId ?? current?.plan_id ?? null;

  const handleSelect = async (plan) => {
    if (loading) return;
    setLoading(true);
    try {
      await onSelectPlan({ planId: plan.id, billing: 'monthly', price: plan.monthly_price });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pricing-wrapper">
      {error && (
        <div className="alert alert-danger d-flex align-items-center justify-content-between mb-3" role="alert">
          <span>{error}</span>
          <button
            type="button"
            className="btn-close ms-3 flex-shrink-0"
            aria-label="Close"
            onClick={() => onClearError?.()}
          />
        </div>
      )}

      <div className="pricing-header">
        <h4>Choose Your Plan</h4>
        <p className="text-muted">Start with our Starter plan or contact us for a custom solution.</p>
      </div>

      <div className="pricing-cards-row">
        {plans.map((plan) => {
          const isCurrent = plan.id === activePlanId;

          return (
            <div className="pricing-card-col" key={plan.id}>
              <Card className={`pricing-card h-100 ${plan.is_popular ? 'popular-plan' : ''}`}>
                {plan.is_popular && <div className="popular-ribbon">Most Popular</div>}

                <Card.Body className="d-flex flex-column p-4">
                  {isCurrent && <div className="current-plan-badge">Current Plan</div>}

                  <h5 className="plan-title">{plan.name}</h5>
                  <p className="plan-description">{plan.description}</p>

                  <div className="plan-price">
                    {plan.is_custom ? (
                      <span className="custom-price">Custom Pricing</span>
                    ) : (
                      <>
                        <span className="price-amount">${plan.monthly_price}</span>
                        <span className="price-cycle"> / month</span>
                        <div className="price-gst">+ {plan.gst_percent}% GST (${plan.gst_amount})</div>
                        <div className="price-total">Total: ${plan.total_price} / month</div>
                      </>
                    )}
                  </div>

                  <ul className="feature-list">
                    {plan.features?.map((feature, i) => (
                      <li key={i}>
                        <i className="ph ph-check-circle feature-icon" /> {feature}
                      </li>
                    ))}
                  </ul>

                  {plan.is_custom ? (
                    <a href="mailto:support@youslotz.com" className="mt-auto">
                      <Button variant="outline-primary" className="w-100">
                        Contact Sales
                      </Button>
                    </a>
                  ) : (
                    <Button
                      className="pricing-btn mt-auto w-100"
                      disabled={(isCurrent && !renewMode) || loading}
                      onClick={() => handleSelect(plan)}
                    >
                      {loading ? (
                        <span className="spinner-border spinner-border-sm" />
                      ) : isCurrent ? (
                        renewMode ? 'Renew Plan' : 'Current Plan'
                      ) : (
                        'Get Started'
                      )}
                    </Button>
                  )}
                </Card.Body>
              </Card>
            </div>
          );
        })}
      </div>
    </div>
  );
}
